import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import {
  CandidateSearchHistoryEntry,
  CandidateSearchHistoryScope,
  CandidateSearchProfileHistoryEntry,
  CandidateSearchProfileSnapshot,
  CandidateSearchTextHistoryEntry
} from '@app/models/candidate-search-history.model';

@Injectable({
  providedIn: 'root'
})
export class CandidateSearchHistoryService {
  readonly changed$ = new Subject<string>();

  private readonly maximumEntries = 20;
  private readonly storagePrefix = 'opogo:candidate-search-history:v1';

  getEntries(scope: CandidateSearchHistoryScope, limit = 5): CandidateSearchHistoryEntry[] {
    if (!this.isValidScope(scope)) {
      return [];
    }

    return this.read(scope)
      .sort((left, right) => Date.parse(right.occurredAt) - Date.parse(left.occurredAt))
      .slice(0, limit);
  }

  recordSearch(scope: CandidateSearchHistoryScope, searchTerm: string): boolean {
    const normalizedTerm = this.normalizeSearchTerm(searchTerm);
    if (!this.isValidScope(scope) || !normalizedTerm) {
      return false;
    }

    const id = `search:${normalizedTerm.toLowerCase()}`;
    const entry: CandidateSearchTextHistoryEntry = {
      id,
      type: 'search',
      searchTerm: normalizedTerm,
      occurredAt: new Date().toISOString()
    };

    return this.saveEntry(scope, entry);
  }

  recordProfile(scope: CandidateSearchHistoryScope, profile: CandidateSearchProfileSnapshot): boolean {
    const candidateId = profile?.candidateId?.trim();
    const displayName = profile?.displayName?.trim();
    if (!this.isValidScope(scope) || !candidateId || !displayName) {
      return false;
    }

    const entry: CandidateSearchProfileHistoryEntry = {
      id: `profile:${candidateId.toLowerCase()}`,
      type: 'profile',
      candidateId,
      displayName,
      imageUrl: profile.imageUrl || null,
      defaultJobRole: profile.defaultJobRole?.trim() || null,
      occurredAt: new Date().toISOString()
    };

    return this.saveEntry(scope, entry);
  }

  remove(scope: CandidateSearchHistoryScope, entryId: string): void {
    if (!this.isValidScope(scope) || !entryId) {
      return;
    }

    const entries = this.read(scope).filter(entry => entry.id !== entryId);
    this.write(scope, entries);
  }

  getScopeKey(scope: CandidateSearchHistoryScope): string {
    if (!this.isValidScope(scope)) {
      return null;
    }

    return `${this.storagePrefix}:${encodeURIComponent(scope.memberUserId)}:${encodeURIComponent(scope.rootClientId)}`;
  }

  private saveEntry(scope: CandidateSearchHistoryScope, entry: CandidateSearchHistoryEntry): boolean {
    const entries = this.read(scope).filter(existing => existing.id !== entry.id);
    entries.unshift(entry);
    return this.write(scope, entries.slice(0, this.maximumEntries));
  }

  private read(scope: CandidateSearchHistoryScope): CandidateSearchHistoryEntry[] {
    const key = this.getScopeKey(scope);
    if (!key || typeof localStorage === 'undefined') {
      return [];
    }

    try {
      const parsed = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.filter(entry => this.isValidEntry(entry));
    } catch {
      return [];
    }
  }

  private write(scope: CandidateSearchHistoryScope, entries: CandidateSearchHistoryEntry[]): boolean {
    const key = this.getScopeKey(scope);
    if (!key || typeof localStorage === 'undefined') {
      return false;
    }

    try {
      localStorage.setItem(key, JSON.stringify(entries));
      this.changed$.next(key);
      return true;
    } catch {
      return false;
    }
  }

  private isValidEntry(entry: any): entry is CandidateSearchHistoryEntry {
    if (!entry || typeof entry.id !== 'string' || typeof entry.occurredAt !== 'string' || Number.isNaN(Date.parse(entry.occurredAt))) {
      return false;
    }

    if (entry.type === 'search') {
      return typeof entry.searchTerm === 'string' && !!entry.searchTerm.trim();
    }

    return entry.type === 'profile'
      && typeof entry.candidateId === 'string'
      && !!entry.candidateId.trim()
      && typeof entry.displayName === 'string'
      && !!entry.displayName.trim();
  }

  private isValidScope(scope: CandidateSearchHistoryScope): boolean {
    return !!scope?.memberUserId?.trim() && !!scope?.rootClientId?.trim();
  }

  private normalizeSearchTerm(searchTerm: string): string {
    return (searchTerm || '').trim().replace(/\s+/g, ' ');
  }
}
